import { computed, getCurrentInstance, onBeforeUnmount, onMounted } from 'vue'
import {
  DEV_GARDIAN_PROFILES_OVERRIDE_STORAGE_KEY,
  readDevGardianOverrideState,
  resolveEffectiveGardianProfilesForDev,
  type DevGardianOverrideState,
} from '~~/utils/devOidcProfileOverride'

type StoredDevOverride = DevGardianOverrideState

/**
 * Simulation locale des profils OIDC (labo / dev uniquement) : entrée stockée **substitue** les claims (y compris liste vide = visiteur).
 * Sync via `localStorage` pour partager l’état entre la page app et l’iframe DevTools.
 */
export function useDevOidcProfilesOverride() {
  const overrideState = useState<StoredDevOverride>('monorga.devOidcProfilesOverride', () => ({
    replaceActive: false,
    profiles: [],
  }))

  function refreshFromStorage(): void {
    overrideState.value = readDevGardianOverrideState()
  }

  function onStorage(event: StorageEvent): void {
    if (event.key !== DEV_GARDIAN_PROFILES_OVERRIDE_STORAGE_KEY && event.key !== null) {
      return
    }
    refreshFromStorage()
  }

  const instance = getCurrentInstance()
  if (instance) {
    onMounted(() => {
      refreshFromStorage()
      if (import.meta.client && import.meta.dev) {
        window.addEventListener('storage', onStorage)
      }
    })
    onBeforeUnmount(() => {
      if (import.meta.client && import.meta.dev) {
        window.removeEventListener('storage', onStorage)
      }
    })
  }

  const isActive = computed(() => import.meta.dev && overrideState.value.replaceActive)

  const overrideProfiles = computed(() => overrideState.value.profiles)

  function setOverrideProfiles(profiles: string[]): void {
    if (!import.meta.client || !import.meta.dev) {
      return
    }
    const filtered = profiles.filter((item) => item.length > 0)
    overrideState.value = { replaceActive: true, profiles: filtered }
    localStorage.setItem(
      DEV_GARDIAN_PROFILES_OVERRIDE_STORAGE_KEY,
      JSON.stringify({ profiles: filtered }),
    )
  }

  function clearOverride(): void {
    if (!import.meta.client) {
      return
    }
    overrideState.value = { replaceActive: false, profiles: [] }
    localStorage.removeItem(DEV_GARDIAN_PROFILES_OVERRIDE_STORAGE_KEY)
  }

  function resolveEffectiveGardianProfiles(oidcProfiles: string[]): string[] {
    return resolveEffectiveGardianProfilesForDev(oidcProfiles, overrideState.value)
  }

  return {
    overrideState,
    overrideProfiles,
    isActive,
    refreshFromStorage,
    setOverrideProfiles,
    clearOverride,
    resolveEffectiveGardianProfiles,
  }
}
